import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const SmoothTransition = ({
  children,
  animationType = "fadeUp",
  delay = 0,
  duration = 1,
  trigger = "top 80%",
  className = "",
  ...props
}) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Define animation presets
    const animations = {
      fadeUp: {
        from: { opacity: 0, y: 50, scale: 0.95 },
        to: { opacity: 1, y: 0, scale: 1 },
      },
      fadeDown: {
        from: { opacity: 0, y: -50, scale: 0.95 },
        to: { opacity: 1, y: 0, scale: 1 },
      },
      fadeLeft: {
        from: { opacity: 0, x: -50, scale: 0.95 },
        to: { opacity: 1, x: 0, scale: 1 },
      },
      fadeRight: {
        from: { opacity: 0, x: 50, scale: 0.95 },
        to: { opacity: 1, x: 0, scale: 1 },
      },
      scale: {
        from: { opacity: 0, scale: 0.8, rotation: 5 },
        to: { opacity: 1, scale: 1, rotation: 0 },
      },
      slideUp: {
        from: { opacity: 0, y: 100, rotationX: 45 },
        to: { opacity: 1, y: 0, rotationX: 0 },
      },
      bounce: {
        from: { opacity: 0, y: 50, scale: 0.8 },
        to: { opacity: 1, y: 0, scale: 1 },
      },
    };

    const animation = animations[animationType] || animations.fadeUp;

    // Set initial state
    gsap.set(element, animation.from);

    // Create scroll trigger animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: trigger,
        end: "top 20%",
        toggleActions: "play none none reverse",
        scrub: false,
      },
    });

    tl.to(element, {
      ...animation.to,
      duration: duration,
      ease: "power3.out",
      delay: delay,
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.trigger === element) {
          trigger.kill();
        }
      });
    };
  }, [animationType, delay, duration, trigger]);

  return (
    <div ref={elementRef} className={className} {...props}>
      {children}
    </div>
  );
};

export default SmoothTransition;
